import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFTextField from "@/components/Input/TextField";
import VLFToggleButtonGroup from "@/components/Input/TogleButtonGroup";
import CreateSelectEntity from "@/components/CreateSelectEntity";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VLFSelectNew from "@/components/Input/SelectNew";
import SaveIcon from "@mui/icons-material/Save";

import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {ClubAPI} from "@/api/club";
import {ScoutReportAPI} from "@/api/scout_report";

import {
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	aptitudeForRole,
	bodyTypeOptions,
	childTypeOptions,
	communicationTypeOptions,
	dateFormat,
	defaultReportOptions,
	originReportOptions,
	AREA_OPTIONS,
	valuationOptions,
	valuationAnalysisOption,
} from "@/utils/constants";
import {
	buildPlayerBody,
	checkRequiredFields,
	getTextField,
	playerToOption,
} from "@/utils/functions";
import {useCategories, useClubs, usePlayers} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

const HandleScoutingReportPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const matchId = new URLSearchParams(useLocation().search).get("matchId");
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(edit ? true : false);
	const [errors, setErrors] = useState([]);
	const [saveOption, setSaveOption] = useState(edit ? "select" : "new");

	const {clubs} = useClubs({setLoading: false, handleNotification});
	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});
	const {players} = usePlayers({
		setLoading: false,
		handleNotification,
		goalkeepers: false,
		prospect: true,
	});

	const [fields, setFields] = useState({
		aptitudeForRole: -1,
		participationLevel: -1,
		childType: -1,
		verbalCommunication: -1,
		bodyType: -1,
		athleticism: -1,
		fatherBodyType: -1,
		motherBodyType: -1,
		reactivity: -1,
	});

	const getReport = async () => {
		setLoading(true);
		try {
			const response = await ScoutReportAPI.getById(id);

			const currentOrigin = originReportOptions.find(
				(option) => option.value === response.origin?.toString(),
			);
			if (currentOrigin) {
				response.origin = {
					value: response.origin,
					label: currentOrigin?.label,
				};
			}

			const currentCategory = categories.find(
				(c) => c.value === response.playerCategoryId,
			);
			if (currentCategory || response.playerCategoryId) {
				response.playerScoutCategory = {
					value: currentCategory?.value || response.playerCategoryId,
					label:
						currentCategory?.label ||
						response.playerCategory?.name ||
						"Categoria",
				};
			}

			if (response.player) {
				response.scoutPlayer = {
					value: response.player?.id,
					label: response.player?.firstName + " " + response.player?.lastName,
				};
			}

			if (response.club) {
				response.scoutClub = {
					value: response.club.id,
					label: response.club.name,
				};
			}

			response.deepening = response.deepening?.split(",");

			setFields((previous) => ({...previous, ...response}));

			if (matchId) {
				await getMatch();
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getMatch = async () => {
		try {
			const response = await MatchAPI.getById(matchId, {seasonId});
			setFields((previous) => ({
				...previous,
				matchObserved: [response.homeClub, response.awayClub]
					.filter(Boolean)
					.join(" - "),
			}));
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		if (edit) {
			getReport();
		} else {
			if (matchId) {
				getMatch();
			}
		}
	}, [edit, id, matchId]);

	const getRequiredFields = () => {
		let reqFields = [
			"reportDate",
			"signalerName",
			"scoutClub",
			"playerScoutCategory",
		];

		if (saveOption === "select") {
			reqFields.push("scoutPlayer");
		} else {
			reqFields.push("lastName", "playerPrimaryRole");
		}

		if (!edit) {
			if (!matchId) {
				reqFields.push("matchObserved");
			}
		}

		return reqFields;
	};

	const createClub = async (name) => {
		try {
			const response = await ClubAPI.create({name});
			setFields({
				...fields,
				scoutClub: {value: response.id, label: response.name},
			});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const createPlayer = async () => {
		try {
			const body = buildPlayerBody({
				firstName: fields?.firstName,
				lastName: fields?.lastName,
				dateOfBirth: fields?.dateOfBirth,
				favoriteFoot: fields?.mainFoot,
				goalkeeper: fields?.playerPrimaryRole?.value === "Goalkeeper",
				primaryRole: fields?.playerPrimaryRole?.label,
			});

			const response = await PlayerAPI.create(body);
			return response;
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const saveReport = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			const body = Object.assign({}, fields);
			body.reportDate = moment(body.reportDate).format("YYYY-MM-DD");
			body.origin = body.origin?.value;
			body.clubId = fields.scoutClub?.value;
			body.playerCategoryId = fields.playerScoutCategory?.value;

			if (saveOption === "new") {
				const playerResponse = await createPlayer();
				body.playerId = playerResponse?.id;
			} else {
				body.playerId = fields.scoutPlayer?.value;
			}

			// In modifica il matchId non e' in query: non va sovrascritto con null.
			if (matchId) {
				body.matchId = matchId;
			}
			body.deepening = body.deepening?.join();

			try {
				const reportData = edit
					? await ScoutReportAPI.update(id, body)
					: await ScoutReportAPI.create(body);
				setTimeout(() => {
					navigate("/scouting/reports/" + (id || reportData?.id));
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		saveReport();
	};

	const goBack = () => {
		if (edit) {
			navigate("/scouting/reports/" + id);
		} else {
			navigate("/scouting/reports");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{label: "Segnalazioni", path: "/scouting/reports"},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: [fields?.player?.firstName, fields?.player?.lastName]
					.filter(Boolean)
					.join(" "),
				path: "/scouting/reports/" + id,
			},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const currentScoutPlayer = players?.filter(
		(p) => p.id === fields?.scoutPlayer?.value,
	)?.[0];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuova segnalazione"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-12 padding-small">
						<VLFCard
							title="Informazioni segnalazione"
							content={
								<div className="row no-margin single-area-container">
									<div className="col-12 col-sm-6 col-xl-4">
										<VLFDatePicker
											label="Data"
											value={fields.reportDate}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, reportDate: value});
											}}
											required
											format="DD-MM-YYYY"
											size="small"
											helperText={
												errors.indexOf("reportDate") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("reportDate") !== -1}
										/>
									</div>
									<div className="col-12 col-sm-6 col-xl-4">
										{getTextField({
											...defaultTextField,
											fieldKey: "signalerName",
											label: "Segnalatore",
											required: true,
										})}
									</div>
									<div className="col-12 col-sm-6 col-xl-4">
										<VLFSelectNew
											options={originReportOptions}
											value={fields.origin}
											onChange={(event) => {
												setFields({
													...fields,
													origin: {label: event.label, value: event.value},
												});
											}}
											label="Origine"
										/>
									</div>
									<div className="col-12 col-sm-6 col-xl-4">
										{getTextField({
											...defaultTextField,
											fieldKey: "matchObserved",
											label: "Partita",
											required: !edit && !matchId,
										})}
									</div>
									<div className="col-12 col-sm-6 col-xl-4">
										<VLFSelectNew
											options={clubs.map((c) => {
												return {
													value: c.id,
													label: c.name,
												};
											})}
											value={fields.scoutClub}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, scoutClub: newValue});
											}}
											onCreateOption={createClub}
											label="Squadra"
											required
											helperText={
												errors.indexOf("scoutClub") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("scoutClub") !== -1}
										/>
									</div>
									<div className="col-12 col-sm-6 col-xl-4">
										<VLFSelectNew
											options={categories}
											sorted={false}
											value={fields.playerScoutCategory}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, playerScoutCategory: newValue});
											}}
											label="Categoria"
											required
											helperText={
												errors.indexOf("playerScoutCategory") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("playerScoutCategory") !== -1}
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-12 padding-small">
						<VLFCard
							title="Informazioni giocatore"
							content={
								<div className="row no-margin single-area-container">
									<div className="col-12 padding-small">
										<CreateSelectEntity
											currentOption={saveOption}
											setCurrentOption={setSaveOption}
										/>
									</div>
									{saveOption === "new" ? (
										<>
											<div className="col-12 col-sm-6 col-xl-4">
												{getTextField({
													...defaultTextField,
													fieldKey: "firstName",
													label: t("name"),
												})}
											</div>
											<div className="col-12 col-sm-6 col-xl-4">
												{getTextField({
													...defaultTextField,
													fieldKey: "lastName",
													label: "Cognome",
													required: true,
												})}
											</div>
											<div className="col-12 col-sm-6 col-xl-4">
												<VLFDatePicker
													label="Data di nascita"
													value={fields.dateOfBirth}
													onChange={(value) => {
														setFields({...fields, dateOfBirth: value});
													}}
													format="DD-MM-YYYY"
												/>
											</div>
											<div className="col-12 col-sm-6 col-xl-4">
												<VLFSelectNew
													options={AREA_OPTIONS}
													sorted={false}
													value={fields.playerPrimaryRole}
													onChange={(newValue) => {
														setFields({
															...fields,
															playerPrimaryRole: newValue,
														});
													}}
													label="Ruolo"
													required
												/>
											</div>
											<div className="col-12 col-sm-6 col-xl-4">
												{getTextField({
													...defaultTextField,
													fieldKey: "mainFoot",
													label: "Piede principale",
												})}
											</div>
										</>
									) : (
										<>
											<div className="col-12 col-sm-6 col-xl-4">
												<VLFSelectNew
													options={players.map((pp) =>
														playerToOption(pp, true),
													)}
													sortKey="sortKey"
													value={fields?.scoutPlayer}
													onChange={(newValue) => {
														setFields({...fields, scoutPlayer: newValue});
													}}
													label="Giocatore"
													required
												/>
											</div>
											{fields?.scoutPlayer && (
												<div className="col-12 col-sm-6 col-xl-4">
													<VLFDatePicker
														label="Data di nascita"
														value={
															currentScoutPlayer &&
															currentScoutPlayer?.dateOfBirth
																? moment(
																		currentScoutPlayer?.dateOfBirth,
																	).format(dateFormat)
																: "---"
														}
														editable={false}
													/>
												</div>
											)}
											{fields?.scoutPlayer && (
												<div className="col-12 col-sm-6 col-xl-4">
													<VLFTextField
														value={
															currentScoutPlayer?.goalkeeper
																? "Portiere"
																: currentScoutPlayer?.primaryRole
														}
														label="Ruolo"
														editable={false}
													/>
												</div>
											)}
										</>
									)}
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-12 padding-small">
						<VLFCard
							title="Dettagli"
							content={
								<div className="row no-margin single-area-container">
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="fatherBodyType"
											value={fields.fatherBodyType?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													fatherBodyType: event.target.value,
												});
											}}
											options={bodyTypeOptions}
											label="Struttura padre"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="motherBodyType"
											value={fields.motherBodyType?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													motherBodyType: event.target.value,
												});
											}}
											options={bodyTypeOptions}
											label="Struttura madre"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6">
										{getTextField({
											...defaultTextField,
											fieldKey: "fatherHeight",
											label: "Altezza padre (cm)",
											type: "number",
										})}
									</div>
									<div className="col-12 col-md-6">
										{getTextField({
											...defaultTextField,
											fieldKey: "motherHeight",
											label: "Altezza madre (cm)",
											type: "number",
										})}
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="bodyType"
											value={fields.bodyType?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													bodyType: event.target.value,
												});
											}}
											options={bodyTypeOptions}
											label="Struttura apparente"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="childType"
											value={fields.childType?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													childType: event.target.value,
												});
											}}
											options={childTypeOptions}
											label="Tipologia di bambino"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="aptitudeForRole"
											value={fields.aptitudeForRole?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													aptitudeForRole: event.target.value,
												});
											}}
											options={aptitudeForRole}
											label="Attitudine al ruolo"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="reactivity"
											value={fields.reactivity?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													reactivity: event.target.value,
												});
											}}
											options={bodyTypeOptions}
											label="Reattività"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="verbalCommunication"
											value={fields.verbalCommunication?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													verbalCommunication: event.target.value,
												});
											}}
											options={communicationTypeOptions}
											label="Comunicazione verbale"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="athleticism"
											value={fields.athleticism?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													athleticism: event.target.value,
												});
											}}
											options={bodyTypeOptions}
											label="Atleticità"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 col-md-6 my-2">
										<VLFToggleButtonGroup
											name="participationLevel"
											value={fields.participationLevel?.toString() || ""}
											onChange={(event) => {
												setFields({
													...fields,
													participationLevel: event.target.value,
												});
											}}
											options={defaultReportOptions}
											label="Partecipazione al gioco della propria squadra"
											size="small"
											rowDirection={true}
										/>
									</div>
									<div className="col-12 my-2">
										<div className="evaluation-choice d-flex flex-column">
											<div className="choice-label">Valutazione</div>
											<div className="choice-options">
												{valuationOptions.map((as, k) => {
													return (
														<div
															key={k}
															className={
																`single-option ${fields?.valuation?.toString() === as.value ? "selected" : ""}` +
																(as.value === "0"
																	? " idoneo"
																	: as.value === "1"
																		? " non-idoneo"
																		: as.value === "2"
																			? " da-rivedere"
																			: "")
															}
															onClick={() => {
																const newValue =
																	fields?.valuation?.toString() === as.value
																		? -1
																		: as.value;
																setFields({
																	...fields,
																	valuation: newValue,
																});
															}}
														>
															{as.label}
														</div>
													);
												})}
											</div>
										</div>
									</div>
									<div className="col-12 my-2">
										<div className="evaluation-choice d-flex flex-column">
											<div className="choice-label">Approfondimento</div>
											<div className="choice-options">
												{valuationAnalysisOption.map((o, k) => {
													return (
														<div
															key={k}
															className={
																"single-option" +
																(fields?.deepening &&
																fields?.deepening?.indexOf(o.value) !== -1
																	? " selected"
																	: "")
															}
															onClick={() => {
																let updatedValue = fields?.deepening
																	? Array.from(fields?.deepening)
																	: [];
																if (updatedValue?.indexOf(o.value) !== -1) {
																	const index = updatedValue.indexOf(o.value);
																	updatedValue.splice(index, 1);
																} else {
																	updatedValue.push(o.value);
																}
																setFields({
																	...fields,
																	deepening: updatedValue,
																});
															}}
														>
															{o.label}
														</div>
													);
												})}
											</div>
										</div>
									</div>
									<div className="col-12 my-2">
										<VLFTextField
											value={fields["notes"] || ""}
											onChange={(e) => {
												setFields({...fields, notes: e.target.value});
											}}
											label="Note"
											size="small"
											multiline
											minRows={3}
											maxRows={3}
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleScoutingReportPage;
